import React, { useState, useRef } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { uploadImageToWasabi } from '../services/wasabiUpload';
import toast from 'react-hot-toast';
import PhotoFramer, { fetchStoredPhoto } from './PhotoFramer';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageUploaded: (imageUrl: string) => void;
  uploadPath: string;
  maxSizeMB?: number;
  className?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageUploaded,
  uploadPath,
  maxSizeMB = 5,
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(currentImageUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The photo being framed before upload (Nicky, 13 Oct 2026): a new pick, or
  // the current photo when "Adjust photo" reframes it.
  const [framing, setFraming] = useState<{ blob: Blob; existing: boolean } | null>(null);
  const [opening, setOpening] = useState(false);
  const inputId = `image-upload-${uploadPath.replace(/[^a-z0-9]+/gi, '-')}`;

  // The shown photo follows the parent's value (e.g. a claimed page's photo arriving later).
  React.useEffect(() => {
    setPreview(currentImageUrl || null);
  }, [currentImageUrl]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Let the same file be chosen again.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Image must be less than ${maxSizeMB}MB`);
      return;
    }

    setFraming({ blob: file, existing: false });
  };

  const adjust = async () => {
    if (!preview || !/^https?:\/\//.test(preview)) return;
    setOpening(true);
    try {
      setFraming({ blob: await fetchStoredPhoto(preview), existing: true });
    } catch {
      toast.error('This photo can’t be adjusted here. Upload it again to frame it.');
    } finally {
      setOpening(false);
    }
  };

  // Save in the editor: upload the framed square to the same place as before.
  const uploadFramed = async (file: File) => {
    setUploading(true);
    try {
      const result = await uploadImageToWasabi(file, uploadPath);
      if (!result.success || !result.imageUrl) throw new Error(result.error || 'Upload failed');
      setPreview(result.imageUrl);
      onImageUploaded(result.imageUrl);
      setFraming(null);
      toast.success('Photo saved');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      throw new Error(error?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Profile Image
      </label>

      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center bg-gray-50 overflow-hidden">
            {preview ? (
              <img src={preview} alt="Preview" className="w-full h-full object-cover rounded-full" />
            ) : (
              <PhotoIcon className="w-8 h-8 text-gray-400" />
            )}
          </div>
        </div>

        <div className="flex-1">
          <input
            ref={fileInputRef}
            id={inputId}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            disabled={uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
          />
          <p className="text-xs text-gray-500 mt-1">
            PNG or JPG. Max {maxSizeMB}MB. You’ll frame it before it’s saved.
            {uploading && <span className="text-blue-600 font-medium"> Uploading...</span>}
          </p>
          {preview && /^https?:\/\//.test(preview) && !uploading && (
            <button
              type="button"
              onClick={() => void adjust()}
              disabled={opening}
              className="mt-2 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50"
            >
              {opening ? 'Opening…' : 'Adjust photo'}
            </button>
          )}
        </div>
      </div>

      {framing && (
        <PhotoFramer
          source={framing.blob}
          existing={framing.existing}
          reuploadInputId={inputId}
          onCancel={() => setFraming(null)}
          onSave={uploadFramed}
        />
      )}
    </div>
  );
};

export default ImageUpload;
